import { flow, get, isEmpty, curry } from 'lodash-es';

// Models
import { InventoryModel, InventoryPaths } from 'client/data/models/inventory';
import { UrlModel, URL_CONTEXT_PATH } from 'client/data/models/url';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { PageModel } from 'client/data/models/page';
import { getReduxModelStateFromStore } from 'client/data/luckdragon/redux/redux-model';
import { VisitorModel } from 'client/data/models/visitor';
import { buildDealerIcoWidgetConfigurationPath, DealerIcoWidgetModel } from 'client/data/models/dealer-ico-widget';
import { buildSearchIdPath, SemanticSearchModel } from 'client/data/models/semantic-search';
import { SEOModel } from 'client/data/models/seo';

// Preloaders
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';

// Utils
import { pageDefinition } from 'site-modules/shared/pages/page';
import { getQuery } from 'client/utils/location';
import { featureFlags } from 'client/utils/feature-flags';
import {
  getPageFiltersValues,
  getPagePublicationState,
  getPagePublicationStateFromUrlContext,
  getUrlPattern,
} from 'site-modules/shared/utils/inventory/srp-utils';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import {
  isNationalSRP,
  isMakeModelLeaseDeals,
  isDealerSRP,
} from 'site-modules/shared/utils/inventory/srp-type-checkers';
import { getSrpSeoObject } from 'site-modules/shared/utils/inventory/srp-seo-object';
import { getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';
import { getVehicleFromSrpInventoryData } from 'site-modules/shared/components/native-ad/utils/get-ad-targeting';
import { setAdsListToPreload } from 'site-modules/shared/components/native-ad/utils/utils';
import { getUsurpImages } from 'client/site-modules/shared/utils/inventory/srp-images';
import { getHasLinkWidget } from 'site-modules/inventory/utils/link-widget';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { handle404Error } from 'site-modules/shared/utils/errors';
import { someInventoryHasCTA } from 'site-modules/inventory/utils/some-inventory-has-cta';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { getNoInventoryDealerlessMessageType } from 'site-modules/shared/components/inventory/utils/no-inventory-message';
import { getMultiMMCriticalKeyValue } from 'site-modules/shared/utils/inventory/multi-make-model';
import { checkCpoMessage } from 'site-modules/shared/utils/cpo-message';
import { isCTAAvailable } from 'site-modules/shared/utils/inventory/usurp-inventory-card';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { getSessionInventoryFilters } from 'site-modules/shared/utils/inventory/session-inventory-filters';
import { getShop5067SrpTopRecipe } from 'site-modules/inventory/components/shop-5067-srp-top/shop-5067-srp-top-utils';

// Actions
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { updateVehicle } from 'client/actions/vehicle';
import { setCriticalCss } from 'client/actions/critical-css';
import { redirect } from 'client/actions/redirect';
import { forceDisableLooseEtag } from 'client/actions/etag';

// Constants
import {
  CORE_SRP_URL_PATTERN,
  MAKE_TYPE_SRP_URL_PATTERN,
} from 'site-modules/shared/constants/inventory/srp-url-patterns';
import { SRP_PAGE_NAME } from 'site-modules/shared/constants/page-names';
import { EAS_LISTING_AD, SRP_AD } from 'site-modules/shared/components/native-ad/utils/constants';

// Components
import { Shop5067SrpTopDecorator } from 'site-modules/inventory/components/shop-5067-srp-top/shop-5067-srp-top-decorator';

import { UsurpContainer } from './usurp-container';

const DESKTOP_NUM_CARDS = 4;
const MOBILE_NUM_CARDS = 2;

const getTopMessageCriticalCssKey = ({ isNoInventoryDealerless, showExpandedRadiusMsg, isSrpNarrativeText }) => {
  if (isNoInventoryDealerless || showExpandedRadiusMsg) {
    return 'box';
  }

  if (isSrpNarrativeText) {
    return 'txt';
  }

  return false;
};

export function getCriticalCssKeys({
  type = '',
  hasCpoMessage = false,
  isNational = false,
  isMakeModelLeaseDealsPage = false,
  inventoryResultsAboveFold = [],
  hasCtas,
  disableInventoryMLPhotoSort,
  showExpandedRadiusMsg = false,
  isMobile = false,
  isSrpNarrativeText = false,
  isNoInventoryDealerless = false,
  multiMM = false,
  shop5067SrpTop = 'ctrl',
}) {
  let typeKey = false;

  if (isMakeModelLeaseDealsPage) {
    typeKey = 'lease';
  } else if (isNew(type)) {
    typeKey = 'newOnly'; // new cards have slightly different layout
  } else if (hasCpoMessage) {
    typeKey = 'cpoMessage'; // we have a zero position cpo component that displays above the results and a cpo-label on the cards
  }

  const hasMultiplePhotosAboveFold = inventoryResultsAboveFold.some(
    vehicle => getUsurpImages(vehicle, disableInventoryMLPhotoSort).photoUrls.length > 1
  );

  // 4 * 2 * 2 * 2 * 3 * 4 * 3 = 1152 values
  const keys = {
    type: typeKey, // 4 values (false, lease, newOnly, cpoMessage)
    ntl: isNational, // 2 values (true, false)
    hasCtas, // 2 values (true, false)
    photos: hasMultiplePhotosAboveFold, // 2 values (true, false)
    topm: getTopMessageCriticalCssKey({
      isNoInventoryDealerless,
      showExpandedRadiusMsg,
      isSrpNarrativeText,
    }), // 3 values (false, box, txt)
    multiMM, // 3 values for desktop (select, addAnother, bubbles); 1 value for mobile (false)
    shop5067SrpTop, // 3 values (ctrl, chal1, chal2)
  };

  if (isMobile) {
    /*
      TODO: delete criticalCssHeight (and clean up keys) after https://edmunds.atlassian.net/browse/PLAT-2314 is merged

      Increasing the critical css height for mobile to confidently get 2 cards into view for all scenarios

      We chose this number to give additional padding for 2 full cards.  This number is closer to fitting
      3 cards.  We have some scenarios where we have a large zero box as well as some line-wrapping
      that can start pushing cards closer and closer to the critical css bounds.

      Example: https://www.edmunds.com/used-certified-pre-owned-toyota-camry/?test-zip=90401

      For this example above, we need 1280 pixels to fit two cards.  However, for safety, we would like to
      add a third card, which takes us to 1750 pixels.

      While this might seem like a lot of extra padding, minimal css will be added because the
      cards are overall very similar.  If we were on a page with a lot of different content, we might
      use smaller height, but since these cards are so similar, the risk doesn't outweigh the reward.
    */
    keys.criticalCssHeight = 1750;
  } else {
    /*
      TODO: delete criticalCssHeight (and clean up keys) after https://edmunds.atlassian.net/browse/PLAT-2314 is merged

      For desktop, we are increasing the height to 1300 so that the "Price and Payment Filter" is within the viewport.
      This filter causes a CLS when user scrolls before the non-critical css is loaded.
    */
    keys.criticalCssHeight = 1300;
  }

  return keys;
}

const updateUrlContextSrpOnMakeType = curry(({ isSrpOnMakeType }, urlContext) =>
  isSrpOnMakeType
    ? {
        ...urlContext,
        isSrpOnMakeType: true,
        urlPattern: MAKE_TYPE_SRP_URL_PATTERN,
        isValidUrl: true,
      }
    : urlContext
);

const updateUrlContextSrpOnUsedCore = curry(({ isSrpOnUsedCore }, urlContext) =>
  isSrpOnUsedCore
    ? {
        ...urlContext,
        isSrpOnUsedCore: true,
        urlPattern: CORE_SRP_URL_PATTERN,
        isValidUrl: true,
      }
    : urlContext
);

const updateUrlContext = (urlContext, { isSrpOnMakeType, isSrpOnUsedCore }) => {
  return flow(
    updateUrlContextSrpOnMakeType({ isSrpOnMakeType }),
    updateUrlContextSrpOnUsedCore({ isSrpOnUsedCore })
  )(urlContext);
};

export const getPreload = async (store, props, params = {}) => {
  const { isSrpOnMakeType, isSrpOnUsedCore, disableCriticalCss } = params;

  const preloader = new ModelPreloader(store, { debugId: 'usurp-definition.jsx' });

  const state = store.getState();
  const isCrawler =
    state?.request?.isSearchBot || state?.request?.isAkamaiCachePrefresh || featureFlags.get('enableBotRender');

  const modelState = getReduxModelStateFromStore(store);
  const urlContext = modelState.get(URL_CONTEXT_PATH, UrlModel);

  const redirectUrl = get(urlContext, 'redirectUrl', '');
  const query = getQuery(props.location);

  if (redirectUrl) {
    const hasQueryString = redirectUrl.includes('?');

    store.dispatch(
      redirect({
        path: isEmpty(query) ? redirectUrl : `${redirectUrl}${hasQueryString ? '&' : '?'}${objectToQueryString(query)}`,
      })
    );

    // Short circuit here, because we are going to redirect (so no point in continuing)
    return preloader.load();
  }

  const updatedUrlContext = updateUrlContext(urlContext, {
    isSrpOnMakeType,
    isSrpOnUsedCore,
  });

  if (updatedUrlContext && !updatedUrlContext.isValidUrl) {
    throw handle404Error({ errorMessage: 'Page not found' });
  }

  const urlPattern = getUrlPattern(updatedUrlContext);

  const location = modelState.get('location', PageModel);
  const prevLocation = modelState.get('previousLocation', PageModel);

  const queryWithRouteFilters = {
    ...query,
    ...(isSrpOnMakeType ? { make: params.make, ...params.typeFilter } : {}),
    ...(isSrpOnUsedCore
      ? {
          make: params.make,
          model: params.model,
          year: params.year,
          inventorytype: 'used,cpo',
        }
      : {}),
  };

  const pageFiltersWithoutSession = {
    ...getFilterFromLocation(queryWithRouteFilters, updatedUrlContext),
    ...(query.wz ? { wz: query.wz } : {}),
  };

  const isRegularSrp = !isDealerSRP(pageFiltersWithoutSession);
  const cookiesSessionFilters = isRegularSrp ? getSessionInventoryFilters() : {};

  const pageFilters = {
    ...getFilterFromLocation(
      {
        ...cookiesSessionFilters,
        ...queryWithRouteFilters,
      },
      updatedUrlContext
    ),
    ...(query.wz ? { wz: query.wz } : {}),
  };

  const isNational = isNationalSRP(updatedUrlContext);
  const { selectedMake, inventoryType } = getPageFiltersValues(modelState, pageFilters);
  const rooftopId = get(pageFilters, 'rooftopId');
  const pagePublicationState =
    inventoryType.length === 0 && !!get(updatedUrlContext, 'vehicle.publicationState')
      ? getPagePublicationStateFromUrlContext(updatedUrlContext)
      : getPagePublicationState(inventoryType);

  let pageNameUpdateValue = `${pagePublicationState}_car_inventory_srp`;

  if (isSrpOnMakeType) {
    pageNameUpdateValue = 'make_type_srp';
  }

  if (isNational) {
    pageNameUpdateValue = `${pageNameUpdateValue}_ntl`;
  }

  if (rooftopId) {
    pageNameUpdateValue = `dealer_${pageNameUpdateValue}`;
  }

  store.dispatch(
    legacyPageUpdate({
      pageName: pageNameUpdateValue,
    })
  );

  preloader.set('searchResultsFilter', InventoryModel, pageFilters);

  if (urlContext !== updatedUrlContext) {
    preloader.set(URL_CONTEXT_PATH, UrlModel, updatedUrlContext);
    // Update url context value in redux before loading inventories,
    // because inventories model relies on the urlContext data from redux
    await preloader.load();
  }

  // Crawlers need facets from SSR; non-crawlers fetch lazily via with-facets HOC.
  // Skip separate facets fetch when: SSR (no prevLocation) or the search params haven't changed
  // (same URL search = same facets, no need to re-fetch. This happens in mode=noss only when preload function is called on the client side too).
  let splitFacetsApiParams = {};

  if (isCrawler) {
    preloader.resolve('facetsSplit', InventoryModel);
  } else if (isEmpty(prevLocation) || prevLocation.search === location.search) {
    // Set facets to empty array to prevent with-facets from trying to fetch them, since they should be the same as the previous ones. This is for the noss case when preload function is called on the client side and we have prevLocation with the same search params.
    splitFacetsApiParams = {
      fetchFacets: false,
      vehicleInfo: true,
      buyOnlineCount: true,
    };
  }

  preloader.set('splitFacetsApiParams', InventoryModel, splitFacetsApiParams);

  if (getHasLinkWidget(updatedUrlContext)) {
    preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
  }

  preloader.resolve('searchResults', InventoryModel);
  if (rooftopId) {
    preloader.resolve(
      buildDealerIcoWidgetConfigurationPath({
        rooftopId,
      }),
      DealerIcoWidgetModel
    );
  }

  const isPrequalified = get(state, 'financing.prequalificationData');

  if (
    modelState.get('enable-seot-4654-remove-mmy-srp-meta-description', FeatureFlagModel) ||
    modelState.get('seot-4897-button-pagination', FeatureFlagModel)
  ) {
    preloader.resolve('seoExperiments', SEOModel);
  }

  return preloader.load().then(async stateModel => {
    // SearchId on the first render is undefined. When this path is resolved,
    // it becomes an empty string. This triggers an extra re-render that affects many internal components.
    // To avoid this, we need to have the correct value on the first render.
    // We cannot update the model path from an empty string to undefined because in this case it loses the dependency for this path.
    // We cannot use the transform function inside od bindToPath because this function is not called if the property has a false value.
    preloader.resolve(buildSearchIdPath(), SemanticSearchModel);

    const inventoryData = stateModel.get('searchResults', InventoryModel);
    const inventoryResults = get(inventoryData, 'inventories.results', []);
    const firstType = get(inventoryType, '[0]', '');
    const isOnlyType = inventoryType.length === 1;

    // Resolve available make with certified program
    const canResolveCpoMessage = checkCpoMessage(pageFilters);
    const cpoDataPath = `cpoData["${makeNiceName(selectedMake)}"]`;
    if (canResolveCpoMessage) {
      preloader.resolve(cpoDataPath, InventoryModel);
    }

    const adsList = new Set([SRP_AD.AD_NAME, EAS_LISTING_AD.AD_NAME]);

    const { pageContext } = state;

    const vehicleData = getVehicleFromSrpInventoryData(inventoryData, pageFilters);
    const vehiclePageData = vehicleData.vehiclePageData;
    const vehicleAdData = vehicleData.vehicleAdData;

    store.dispatch(updateVehicle(vehiclePageData));

    if (!rooftopId) {
      // rooftopId - is a dealer page, don't show ads
      // when page is make-srp ot bt type, vehicleAdData is defined otherwise is null
      setAdsListToPreload(preloader, state, adsList, vehicleAdData || vehiclePageData, {
        pageLocation: pageContext.location,
        spaMode: true,
      });

      preloader.resolve(InventoryPaths.getAdTargeting(), InventoryModel);
    }

    const zipCode = modelState.get('location.zipCode', VisitorModel);

    /**
     * enable critical css when there is:
     *
     *   1. Is not disabled in params
     *   2. has search results cards
     *   3. at least one filter is selected
     *   4. the user has a valid US zip code
     *   5. is not "prequalified" for capital one
     *   6. is not dealerSrp
     */
    const enableCriticalCss =
      !disableCriticalCss &&
      !!get(inventoryData, 'inventories.results.length') &&
      !isEmpty(pageFilters) &&
      zipCode &&
      !isPrequalified &&
      !rooftopId;

    const isMakeModelLeaseDealsPage = isMakeModelLeaseDeals(urlPattern);

    // for desktop = 4
    // for mobile = 2
    const numCardsAboveFold = state.mobile ? MOBILE_NUM_CARDS : DESKTOP_NUM_CARDS;

    const disableInventoryMLPhotoSort = modelState.get('disable-inventory-ml-photo-sort', FeatureFlagModel);
    const disableDrivewayNewCarLeadForms = modelState.get('disableDrivewayNewCarLeadForms', FeatureFlagModel);
    const pageNumber = get(inventoryData, 'inventories.pageNumber');
    const showExpandedRadiusMsg =
      !!get(inventoryData, 'attributes.expandedRadiusDueToLowResults', false) &&
      get(inventoryData, 'attributes.radius') === 6000 &&
      pageNumber === 1;
    const attributes = get(inventoryData, 'attributes', {});
    const narrativeItems = get(inventoryData, 'seoInfo.narrativeWidget.items', []);
    const isSrpNarrativeText = narrativeItems.length > 0;

    const isNoInventoryDealerless = !!getNoInventoryDealerlessMessageType({
      makeSlug: get(attributes, 'vehicle.make.niceId', ''),
      selectedCarTypes: get(pageFilters, 'inventoryType', []),
    });

    const firstInventory = get(inventoryData, 'inventories.results[0]');

    if (firstInventory) {
      const { photoUrls } = getUsurpImages(firstInventory, disableInventoryMLPhotoSort);
      const imageLink = get(photoUrls, '[0]');

      const preloadHeroImageAsset = {
        href: imageLink,
        as: 'image',
      };

      preloader.set('server.preloadedAssets', PageModel, [preloadHeroImageAsset]);
    }

    const preloaderResponse = await preloader.load();
    /* PLEASE DON'T PRELOAD ANY DATA IN THIS BLOCK.
       PLEASE MAKE YOUR CALLS IN PARALLEL.
       THIS SECTION WAS ONLY ADDED FOR CRITICAL CSS KEY DISPATCH. */

    const isTED7440Enabled =
      ExperimentUtil.getForcedOrAssignedRecipeName({
        state,
        campaignName: 'TED-7440-Basic-Listing-Lead-Vaporware',
        defaultVal: 'ctrl',
      }) === 'chal-1';

    const inventoryResultsAboveFold = inventoryResults.slice(0, numCardsAboveFold);
    const isAboveFoldWithCta = inventoryResultsAboveFold.some(vehicle =>
      isCTAAvailable({ vehicle, disableDrivewayNewCarLeadForms, isTED7440Enabled })
    );
    const hasCtas =
      someInventoryHasCTA({
        inventories: inventoryResultsAboveFold,
        isDealerSrp: !!rooftopId,
      }) && isAboveFoldWithCta;
    const hasCpoMessage = preloaderResponse.get(cpoDataPath, InventoryModel)?.hasCpoMessage;

    const criticalCssKeys = enableCriticalCss
      ? getCriticalCssKeys({
          type: isOnlyType ? firstType : '',
          hasCpoMessage,
          isNational,
          isMakeModelLeaseDealsPage,
          inventoryResultsAboveFold,
          hasCtas,
          disableInventoryMLPhotoSort,
          showExpandedRadiusMsg,
          isMobile: state.mobile,
          isSrpNarrativeText,
          isNoInventoryDealerless,
          shop5067SrpTop: getShop5067SrpTopRecipe(state),
          multiMM:
            !state.mobile &&
            getMultiMMCriticalKeyValue({
              makesNum: get(pageFilters, 'make', []).length,
              modelsNum: get(pageFilters, 'model', []).length,
            }),
        })
      : false;

    store.dispatch(setCriticalCss(criticalCssKeys));

    if (isNational) {
      store.dispatch(forceDisableLooseEtag(true));
    }

    /* PLEASE DON'T PRELOAD ANY DATA IN THIS BLOCK.
       PLEASE MAKE YOUR CALLS IN PARALLEL.
       THIS SECTION WAS ONLY ADDED FOR CRITICAL CSS KEY DISPATCH. */
    return preloaderResponse;
  });
};

export const pageDefinitionConfiguration = {
  name: SRP_PAGE_NAME,
  category: 'car_inventory',
  seo: getSrpSeoObject,
  preload: getPreload,
  component: UsurpContainer,
  decorator: Shop5067SrpTopDecorator,
  options: {
    disableScrollToTop: true,
    hasVehicleContext: true,
    vehicleContextChange: true,
    adsSpaMode: true,
    disableAdRefreshOnScroll: true,
  },
};

export default pageDefinition(pageDefinitionConfiguration);
